import { memo, useCallback } from 'react';
import { PrefetchLink } from '@/components/shared/PrefetchLink';
import { Calendar, MapPin, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Event } from '@/lib/types';
import { format } from 'date-fns';
import { downloadICalFile } from '@/utils/ical';
import { transformImageUrl, buildSrcSet, fallbackToOrigin } from '@/lib/imageUrl';

interface EventCardProps {
  event: Event;
  variant?: 'default' | 'compact';
}

export const EventCard = memo(function EventCard({ event, variant = 'default' }: EventCardProps) {
  const isCompact = variant === 'compact';

  const handleDownloadICS = useCallback(() => {
    downloadICalFile(event);
  }, [event]);

  return (
    <Card className="group overflow-hidden transition-all hover:shadow-lg">
      {!isCompact && event.image_url && (
        <PrefetchLink to={`/events/${event.slug}`} prefetchOnVisible className="block overflow-hidden">
          <div className="aspect-[16/9] bg-muted">
            <img
              src={transformImageUrl(event.image_url, { width: 800 })}
              srcSet={buildSrcSet(event.image_url)}
              alt={event.title}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
              decoding="async"
              width={640}
              height={360}
              sizes="(max-width: 768px) 100vw, 50vw"
              style={{ contentVisibility: 'auto' }}
              onError={(e) => fallbackToOrigin(e, event.image_url)}
            />
          </div>
        </PrefetchLink>
      )}

      <CardContent className={isCompact ? "p-3 md:p-4" : "p-4 md:p-5"}>
        <div className="flex items-start gap-3 md:gap-4">
          {/* Date box */}
          <div className="flex-shrink-0 w-11 h-11 md:w-14 md:h-14 bg-primary text-primary-foreground rounded-lg flex flex-col items-center justify-center">
            <span className="text-[10px] md:text-xs font-medium uppercase">
              {format(new Date(event.event_date), 'MMM')}
            </span>
            <span className="text-base md:text-xl font-bold">
              {format(new Date(event.event_date), 'd')}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <PrefetchLink to={`/events/${event.slug}`} prefetchOnVisible>
              <h3 className="font-display font-bold text-sm md:text-lg text-headline group-hover:text-primary transition-colors line-clamp-2">
                {event.title}
              </h3>
            </PrefetchLink>

            <div className="mt-1.5 md:mt-2 space-y-0.5 md:space-y-1 text-[11px] md:text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">
                  <span className="hidden sm:inline">{format(new Date(event.event_date), 'EEEE, MMMM d, yyyy')}</span>
                  <span className="sm:hidden">{format(new Date(event.event_date), 'MMM d, yyyy')}</span>
                </span>
              </div>
              
              {event.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}
            </div>

            {event.event_type && (
              <Badge variant="secondary" className="mt-1.5 md:mt-2 text-[10px] md:text-xs">
                {event.event_type}
              </Badge>
            )}

            {!isCompact && event.description && (
              <p className="mt-2 md:mt-3 text-xs md:text-sm text-body-text line-clamp-2">
                {event.description}
              </p>
            )}

            {/* Actions - Mobile optimized */}
            <div className="mt-3 md:mt-4 flex flex-col sm:flex-row gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleDownloadICS}
                className="text-xs md:text-sm h-8 md:h-9"
              >
                <Download className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1" aria-hidden="true" />
                Add to Calendar
              </Button>
              
              {event.external_link && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  asChild
                  className="text-xs md:text-sm h-8 md:h-9"
                >
                  <a 
                    href={event.external_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label={`Learn more about ${event.title} - opens in new tab`}
                  >
                    <ExternalLink className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1" aria-hidden="true" />
                    Learn More
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});